import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { roleLabel } from "@/lib/utils-msip";
import { fmtIST } from "@/components/FreshnessFooter";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground";
const STATUS = {
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-800" },
  inactive: { label: "Inactive 7d+", cls: "bg-amber-50 text-amber-800" },
  never: { label: "Never logged in", cls: "bg-red-50 text-red-700" },
};
const FILTERS = [{ v: "all", l: "All" }, { v: "active", l: "Active" }, { v: "inactive", l: "Inactive 7d+" }, { v: "never", l: "Never logged in" }];

const relDays = (iso) => {
  if (!iso) return "—";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d === 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
};

function Stat({ label, value, testId, tone = "" }) {
  return (
    <div className="border bg-card rounded-md px-4 py-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`font-heading text-2xl font-extrabold ${tone}`}>{value}</div>
    </div>
  );
}

export function UsageAdmin() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [openDay, setOpenDay] = useState(null);
  useEffect(() => { api.get("/admin/usage", { params: { days: 30 } }).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!data) return <div className="text-sm text-muted-foreground py-10 text-center">Loading usage…</div>;

  const users = data.users.filter((u) => filter === "all" || u.status === filter).sort((a, b) => (b.last_seen_at || "").localeCompare(a.last_seen_at || ""));
  const s = data.summary;
  return (
    <div className="space-y-6" data-testid="admin-usage">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        <Stat label="Users" value={s.total_users} testId="usage-total-users" />
        <Stat label="Active today" value={s.active_today} testId="usage-active-today" tone="text-emerald-700" />
        <Stat label="Active last 7 days" value={s.active_7d} testId="usage-active-7d" />
        <Stat label="Inactive 7d+" value={s.inactive_7d} testId="usage-inactive" tone="text-amber-700" />
        <Stat label="Never logged in" value={s.never_logged_in} testId="usage-never" tone="text-red-700" />
      </div>

      <div className="border bg-card rounded-md overflow-auto" data-testid="usage-users-table">
        <div className="px-4 py-3 border-b flex items-center gap-3 flex-wrap">
          <h3 className="font-heading text-sm font-semibold">Who is using it</h3>
          <div className="flex items-center rounded-md border overflow-hidden text-xs ml-auto">
            {FILTERS.map((f) => (
              <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`usage-filter-${f.v}`}
                className={`px-2.5 py-1 font-medium transition-colors duration-200 ${filter === f.v ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`}>{f.l}</button>
            ))}
          </div>
        </div>
        <table className="w-full text-xs">
          <thead className="border-b"><tr>{["User", "Role", "Scope", "Last login", "Last active", "Logins", "Active days (30d)", "Status"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b last:border-0 hover:bg-muted/50" data-testid={`usage-row-${u.email}`}>
                <td className="px-3 py-1.5"><div className="font-medium">{u.name || u.email}</div><div className="text-muted-foreground">{u.email}</div></td>
                <td className="px-3 py-1.5">{roleLabel(u)}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{u.scope}</td>
                <td className="px-3 py-1.5 whitespace-nowrap" title={u.last_login_at || ""}>{fmtIST(u.last_login_at)}</td>
                <td className="px-3 py-1.5 whitespace-nowrap" title={u.last_seen_at || ""} data-testid={`usage-last-seen-${u.email}`}>{relDays(u.last_seen_at)}</td>
                <td className="px-3 py-1.5 num">{u.login_count}</td>
                <td className="px-3 py-1.5 num">{u.active_days}</td>
                <td className="px-3 py-1.5"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS[u.status].cls}`} data-testid={`usage-status-${u.email}`}>{STATUS[u.status].label}</span></td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No users in this group</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="border bg-card rounded-md overflow-auto" data-testid="usage-daily-table">
        <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">Daily usage — last {data.days} days</h3><p className="text-[11px] text-muted-foreground">Click a day to see who was active. Tracking started when this report was switched on.</p></div>
        <table className="w-full text-xs">
          <thead className="border-b"><tr>{["Date", "Active users", "Logins", "Who"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {data.daily.map((d) => (
              <tr key={d.date} className={`border-b last:border-0 cursor-pointer hover:bg-muted/50 ${d.active_users === 0 ? "text-muted-foreground" : ""}`} onClick={() => setOpenDay(openDay === d.date ? null : d.date)} data-testid={`usage-day-${d.date}`}>
                <td className="px-3 py-1.5 whitespace-nowrap font-medium">{new Date(`${d.date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" })}</td>
                <td className="px-3 py-1.5 num" data-testid={`usage-day-count-${d.date}`}>{d.active_users}</td>
                <td className="px-3 py-1.5 num">{d.logins}</td>
                <td className="px-3 py-1.5 text-muted-foreground">
                  {openDay === d.date || d.users.length <= 3 ? d.users.join(", ") || "—" : `${d.users.slice(0, 3).join(", ")} +${d.users.length - 3} more`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
